import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { authController } from '@/states/authStateController';
import { useAuth } from '@chainlit/react-client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowUnsubscribed?: boolean;
}

export default function ProtectedRoute({
  children,
  allowUnsubscribed = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isReady } = useAuth();
  const { user, isLoading } = authController.useState(['user', 'isLoading']);

  // Show loading state while checking authentication
  if ((!user && isLoading) || !isReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if no user is logged in
  if (!user || !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to payment if subscription is not active (unless explicitly allowed)
  if (!allowUnsubscribed) {
    const hasActiveSubscription = user.is_subscribed;
    const hasValidExpiryDate = user.subscription_expiry_date
      ? new Date(user.subscription_expiry_date) > new Date()
      : false;

    // Allow access only if user is subscribed AND the expiry date is still valid
    const isActive = hasActiveSubscription || hasValidExpiryDate;
    if (!isActive) {
      return <Navigate to="/payment" replace />;
    }
  }

  // User is authenticated (and subscribed, or route allows unsubscribed users), render children
  return <>{children}</>;
}
